// Admin tab — Partners management: add/edit/delete + logo upload + reorder
import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";

const COLOR_PRESETS = [
  { value: "var(--lapis)", label: "Lapis" },
  { value: "var(--moss)", label: "Mousse" },
  { value: "var(--cinnabar)", label: "Cinabre" },
  { value: "var(--gold)", label: "Or" },
  { value: "var(--ink)", label: "Encre" },
];

export default function PartnersTab({ locale }) {
  const isFr = locale === "fr";
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null or partner object

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/partners");
      setItems(data.items || []);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggleActive = async (p) => {
    await api.patch(`/admin/partners/${p.id}`, { active: !p.active });
    load();
  };

  const remove = async (p) => {
    if (!window.confirm(isFr ? `Retirer définitivement « ${p.name} » ?` : `Permanently remove "${p.name}"?`)) return;
    await api.delete(`/admin/partners/${p.id}`);
    load();
  };

  const move = async (p, delta) => {
    const sorted = [...items].sort((a, b) => (a.order || 0) - (b.order || 0));
    const idx = sorted.findIndex((x) => x.id === p.id);
    const swap = sorted[idx + delta];
    if (!swap) return;
    await Promise.all([
      api.patch(`/admin/partners/${p.id}`, { order: swap.order }),
      api.patch(`/admin/partners/${swap.id}`, { order: p.order }),
    ]);
    load();
  };

  return (
    <div data-testid="partners-tab" className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="font-display text-2xl">{isFr ? "Partenaires" : "Partners"}</h2>
          <p className="font-body text-sm" style={{ color: "var(--muted)" }}>
            {isFr ? "Affichés publiquement sur la page Fêtes de saint Léonard." : "Displayed publicly on the Feasts page."}
          </p>
        </div>
        <button data-testid="partner-new" onClick={() => setEditing({})} className="btn btn-primary">
          + {isFr ? "Nouveau partenaire" : "New partner"}
        </button>
      </div>

      {loading && <p className="text-center" style={{ color: "var(--muted)" }}>{isFr ? "Chargement…" : "Loading…"}</p>}

      {!loading && items.length === 0 && (
        <p className="text-center bg-white border p-8" style={{ borderColor: "var(--border)", color: "var(--muted)" }}>
          {isFr ? "Aucun partenaire enregistré." : "No partners yet."}
        </p>
      )}

      <div className="bg-white border" style={{ borderColor: "var(--border)" }}>
        <table className="w-full font-body text-sm">
          <thead>
            <tr style={{ background: "var(--parchment-2)" }}>
              <th className="text-center p-3 font-caps text-[0.65rem]" style={{ letterSpacing: "0.18em", color: "var(--muted)" }}>{isFr ? "ORDRE" : "ORDER"}</th>
              <th className="text-left p-3 font-caps text-[0.65rem]" style={{ letterSpacing: "0.18em", color: "var(--muted)" }}>LOGO</th>
              <th className="text-left p-3 font-caps text-[0.65rem]" style={{ letterSpacing: "0.18em", color: "var(--muted)" }}>{isFr ? "NOM" : "NAME"}</th>
              <th className="text-left p-3 font-caps text-[0.65rem]" style={{ letterSpacing: "0.18em", color: "var(--muted)" }}>URL</th>
              <th className="text-center p-3 font-caps text-[0.65rem]" style={{ letterSpacing: "0.18em", color: "var(--muted)" }}>{isFr ? "STATUT" : "STATUS"}</th>
              <th className="text-right p-3 font-caps text-[0.65rem]" style={{ letterSpacing: "0.18em", color: "var(--muted)" }}></th>
            </tr>
          </thead>
          <tbody>
            {[...items].sort((a, b) => (a.order || 0) - (b.order || 0)).map((p, idx, arr) => (
              <tr key={p.id} data-testid={`partner-row-${p.slug}`}
                  className="border-t" style={{ borderColor: "var(--border)" }}>
                <td className="p-3 text-center">
                  <div className="inline-flex flex-col gap-1">
                    <button onClick={() => move(p, -1)} disabled={idx === 0}
                            className="text-xs disabled:opacity-30" title={isFr ? "Monter" : "Up"}>▲</button>
                    <button onClick={() => move(p, +1)} disabled={idx === arr.length - 1}
                            className="text-xs disabled:opacity-30" title={isFr ? "Descendre" : "Down"}>▼</button>
                  </div>
                </td>
                <td className="p-3">
                  {p.logo_url ? (
                    <img src={p.logo_url} alt={p.name} className="h-12 max-w-[80px] object-contain border" style={{ borderColor: "var(--border)" }} />
                  ) : (
                    <div className="h-12 w-12 rounded-full inline-flex items-center justify-center font-display text-lg"
                         style={{ background: p.color || "var(--lapis)", color: "white" }}>{p.name.charAt(0)}</div>
                  )}
                </td>
                <td className="p-3 font-display" style={{ color: "var(--ink)" }}>{p.name}</td>
                <td className="p-3 text-xs">
                  {p.url ? (
                    <a href={p.url} target="_blank" rel="noopener noreferrer" className="link-underline" style={{ color: "var(--lapis)" }}>{p.url}</a>
                  ) : <span style={{ color: "var(--muted)" }}>—</span>}
                </td>
                <td className="p-3 text-center">
                  <button data-testid={`partner-active-${p.slug}`}
                          onClick={() => toggleActive(p)}
                          className="font-caps text-[0.6rem] px-2.5 py-1 border"
                          style={{ borderColor: p.active ? "var(--moss)" : "var(--muted)", color: p.active ? "var(--moss)" : "var(--muted)", letterSpacing: "0.15em" }}>
                    {p.active ? (isFr ? "ACTIF" : "ACTIVE") : (isFr ? "MASQUÉ" : "HIDDEN")}
                  </button>
                </td>
                <td className="p-3 text-right">
                  <div className="inline-flex gap-1">
                    <button data-testid={`partner-edit-${p.slug}`} onClick={() => setEditing(p)}
                            className="font-caps text-[0.6rem] px-2.5 py-1 border"
                            style={{ borderColor: "var(--lapis)", color: "var(--lapis)", letterSpacing: "0.15em" }}>
                      {isFr ? "MODIFIER" : "EDIT"}
                    </button>
                    <button data-testid={`partner-delete-${p.slug}`} onClick={() => remove(p)}
                            className="font-caps text-[0.6rem] px-2.5 py-1 border"
                            style={{ borderColor: "var(--cinnabar)", color: "var(--cinnabar)", letterSpacing: "0.15em" }}>
                      {isFr ? "SUPPR." : "DEL."}
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <PartnerEditor
          partner={editing}
          locale={locale}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function PartnerEditor({ partner, locale, onClose, onSaved }) {
  const isFr = locale === "fr";
  const isNew = !partner.id;
  const [form, setForm] = useState({
    name: partner.name || "",
    url: partner.url || "",
    color: partner.color || "var(--lapis)",
    active: partner.active !== false,
  });
  const [logoFile, setLogoFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault(); setErr(""); setBusy(true);
    try {
      let id = partner.id;
      if (isNew) {
        const { data } = await api.post("/admin/partners", form);
        id = data.id;
      } else {
        await api.patch(`/admin/partners/${id}`, form);
      }
      if (logoFile) {
        const fd = new FormData();
        fd.append("file", logoFile);
        await api.post(`/admin/partners/${id}/logo`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }
      onSaved();
    } catch (ex) {
      setErr(ex?.response?.data?.detail || "Erreur");
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="partner-editor"
         onClick={onClose}
         className="fixed inset-0 z-50 flex items-center justify-center p-4"
         style={{ background: "rgba(20,16,12,0.6)" }}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit}
            className="bg-white border max-w-lg w-full p-6 space-y-4"
            style={{ borderColor: "var(--border)", boxShadow: "0 20px 40px rgba(0,0,0,0.3)" }}>
        <div className="font-caps text-[0.65rem]" style={{ color: "var(--gold)", letterSpacing: "0.25em" }}>
          {isNew ? (isFr ? "NOUVEAU PARTENAIRE" : "NEW PARTNER") : (isFr ? "MODIFIER" : "EDIT")}
        </div>

        <label className="block">
          <span className="label-caps">{isFr ? "NOM *" : "NAME *"}</span>
          <input data-testid="partner-name" required className="input-classic mt-1" style={{ width: "100%" }}
                 value={form.name} onChange={(e) => setForm((s) => ({ ...s, name: e.target.value }))} />
        </label>

        <label className="block">
          <span className="label-caps">{isFr ? "SITE WEB" : "WEBSITE"}</span>
          <input data-testid="partner-url" type="url" placeholder="https://…"
                 className="input-classic mt-1" style={{ width: "100%" }}
                 value={form.url} onChange={(e) => setForm((s) => ({ ...s, url: e.target.value }))} />
        </label>

        <label className="block">
          <span className="label-caps">{isFr ? "COULEUR ACCENT (bord du haut)" : "ACCENT COLOR"}</span>
          <select data-testid="partner-color" className="input-classic mt-1" style={{ width: "100%" }}
                  value={form.color} onChange={(e) => setForm((s) => ({ ...s, color: e.target.value }))}>
            {COLOR_PRESETS.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
          </select>
        </label>

        <label className="block">
          <span className="label-caps">LOGO (PNG, JPG, WEBP — max 20 Mo)</span>
          <input data-testid="partner-logo-file" type="file" accept=".png,.jpg,.jpeg,.webp,.gif"
                 className="mt-1 font-body text-sm"
                 onChange={(e) => setLogoFile(e.target.files?.[0] || null)} />
          {partner.logo_url && !logoFile && (
            <img src={partner.logo_url} alt="" className="h-16 mt-2 border" style={{ borderColor: "var(--border)" }} />
          )}
        </label>

        <label className="flex items-center gap-2 font-body text-sm">
          <input type="checkbox" checked={form.active}
                 onChange={(e) => setForm((s) => ({ ...s, active: e.target.checked }))} />
          {isFr ? "Afficher publiquement sur le site" : "Display publicly"}
        </label>

        {err && <p className="text-sm" style={{ color: "var(--cinnabar)" }}>{err}</p>}

        <div className="flex gap-2 pt-2">
          <button type="button" onClick={onClose} className="btn btn-outline" style={{ flex: 1 }}>
            {isFr ? "Annuler" : "Cancel"}
          </button>
          <button type="submit" data-testid="partner-save" disabled={busy} className="btn btn-primary" style={{ flex: 1 }}>
            {busy ? "…" : (isFr ? "Enregistrer" : "Save")}
          </button>
        </div>
      </form>
    </div>
  );
}
